import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, setCustomerAuth } from "@/lib/auth";
import { tableStore } from "@/lib/cart";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowLeft, LogOut, Wallet, Receipt, RefreshCw } from "lucide-react";

export default function CustomerProfile() {
  const auth = useAuth();
  const nav = useNavigate();
  const tbl = tableStore.get();
  const [me, setMe] = useState(null);
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!auth) { nav("/login"); return; }
    let cancelled = false;
    setErr(null);
    setMe(null);
    // A 401 here means the stored session is stale — the api response interceptor
    // clears it and redirects to /login, so we only surface NON-401 failures (offline,
    // 5xx) as a retryable error. Either way the page never sits on "Loading…" forever.
    api.get("/auth/me")
      .then((r) => { if (!cancelled) setMe(r.data); })
      .catch((e) => {
        if (cancelled || e?.response?.status === 401) return;
        setErr("We couldn't load your profile.");
      });
    // Orders are secondary — a failure here must not blank the whole page.
    api.get("/orders/customer/mine")
      .then((r) => { if (!cancelled) setOrders(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setOrders([]); });
    return () => { cancelled = true; };
  }, [auth, nav, reloadKey]);

  if (err) return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain grid place-items-center p-8">
      <div className="text-center max-w-sm">
        <div className="w-14 h-14 rounded-2xl bg-[var(--qd-cream)] grid place-items-center mx-auto mb-4"><RefreshCw className="w-7 h-7 text-[var(--qd-primary)]" aria-hidden /></div>
        <h1 className="font-display text-2xl font-bold tracking-tight mb-1">Couldn't load your profile</h1>
        <p className="text-sm text-[var(--qd-muted)] mb-6">{err} Check your connection and try again.</p>
        <div className="flex items-center justify-center gap-3">
          <Button data-testid="profile-retry" onClick={() => setReloadKey((k) => k + 1)} className="rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">Retry</Button>
          <Link to="/"><Button variant="outline" className="rounded-full">Home</Button></Link>
        </div>
      </div>
    </div>
  );

  if (!me) return <div data-testid="profile-loading" className="min-h-screen grid place-items-center text-[var(--qd-muted)]">Loading…</div>;

  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain pb-8">
      <header className="p-5 flex items-center justify-between">
        <button data-testid="profile-back" onClick={() => nav(tbl ? `/m/${tbl.token}` : "/")} aria-label="Back" className="w-11 h-11 rounded-full bg-white grid place-items-center border border-[var(--qd-border)]"><ArrowLeft className="w-4 h-4" aria-hidden /></button>
        <h1 className="font-display font-bold text-xl">Profile</h1>
        <button data-testid="profile-logout" onClick={() => { setCustomerAuth(null); nav("/"); }} aria-label="Log out" className="w-11 h-11 rounded-full bg-white grid place-items-center border border-[var(--qd-border)]"><LogOut className="w-4 h-4" aria-hidden /></button>
      </header>

      <div className="px-5 space-y-4">
        <div className="bg-white rounded-3xl p-5 border border-[var(--qd-border)]">
          <div className="font-display text-2xl font-bold">{me.user?.name || "Guest"}</div>
          <div className="text-sm text-[var(--qd-muted)]">{me.user?.email}</div>
        </div>

        {/* Loyalty is earned & spent PER restaurant — show each wallet, not a pooled total. */}
        <div className="bg-gradient-to-br from-[var(--qd-primary)] to-[#B8482D] text-white rounded-3xl p-6 qd-shadow-lift">
          <div className="flex items-center gap-2 text-xs font-mono-alt uppercase tracking-widest opacity-80"><Wallet className="w-3.5 h-3.5" aria-hidden /> Loyalty points</div>
          {(me.wallets || []).filter((w) => (w.balance || 0) > 0).length === 0 ? (
            <div data-testid="profile-wallet-empty" className="mt-3 text-sm opacity-90">Order at any restaurant to start earning — points are saved separately for each place you visit.</div>
          ) : (
            <div className="mt-3 space-y-3" data-testid="profile-wallets">
              {(me.wallets || []).filter((w) => (w.balance || 0) > 0).map((w) => (
                <div key={w.restaurant_id} data-testid={`wallet-${w.restaurant_id}`} className="flex items-baseline justify-between gap-3">
                  <span className="opacity-90 truncate">{w.restaurant_name}</span>
                  <span className="font-display font-black text-3xl shrink-0">{w.balance}<span className="text-sm font-bold opacity-80"> pts</span></span>
                </div>
              ))}
              <div className="text-[11px] opacity-70 pt-2 border-t border-white/20">Points are specific to each restaurant — redeem them where you earned them.</div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl p-5 border border-[var(--qd-border)]">
          <div className="flex items-center gap-2 text-xs font-mono-alt uppercase tracking-widest text-[var(--qd-muted)] mb-3"><Receipt className="w-3.5 h-3.5" /> Recent Orders</div>
          {orders.length === 0 ? <div className="text-sm text-[var(--qd-muted)]">No orders yet</div> : (
            <div className="space-y-2">
              {orders.map((o) => (
                <Link key={o.id} to={`/order/${o.id}`} data-testid={`profile-order-${o.id}`} className="flex justify-between items-center p-3 rounded-xl bg-[var(--qd-bg)] hover:bg-[var(--qd-cream)] transition-colors">
                  <div>
                    <div className="font-semibold text-sm">#{o.id.slice(0, 6).toUpperCase()} · {o.table_label}</div>
                    <div className="text-xs text-[var(--qd-muted)] capitalize">{o.status}{Number.isFinite(Date.parse(o.created_at)) ? ` · ${new Date(o.created_at).toLocaleDateString()}` : ""}</div>
                  </div>
                  <div className="font-display font-bold">{formatMoney(o.total)}</div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
